import { Button, ButtonGroup } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { ALL_LOCALES, type Locale } from '@lims/shared-types';
import { changeLanguage, currentLocale } from '../i18n/index.js';

/**
 * Interface language switcher.
 *
 * Switching flips the document direction (RTL for Arabic, LTR for English) as
 * well as the text, so the whole layout mirrors rather than only translating.
 *
 * EACH BUTTON CARRIES ITS OWN `lang`, and it has to. Every label here is an
 * ENDONYM — the Arabic option reads "العربية" and the English one "English" in
 * BOTH catalogues — so that somebody who cannot read the current language can
 * still find their own. That means one of these two buttons is always in a
 * language the surrounding document does not declare, and a screen reader
 * following the page language mispronounces it. Marking each button with the
 * language it is actually written in fixes both directions at once, not just
 * Arabic-inside-English.
 *
 * `surface` says what the switcher sits on. On the navy rail the unselected
 * option must use the rail's own ink and edge: with the page ink it measured
 * 1.06:1 against the rail in a real browser, which made the other language —
 * the one a reader who cannot read this page is looking for — invisible.
 */
export function LanguageSwitcher({
  surface = 'light',
  onChange,
}: {
  readonly surface?: 'light' | 'dark';
  /** Called after the interface switches, so a caller can persist the choice (the portal saves it to the account). */
  readonly onChange?: (locale: Locale) => void;
}) {
  const { t } = useTranslation();
  const active = currentLocale();
  const unselectedClass = surface === 'dark' ? 'border-navy-edge text-navy-ink' : undefined;

  const labelFor = (locale: Locale): string =>
    locale === 'ar' ? t('common.arabic') : t('common.english');

  return (
    <ButtonGroup size="sm" variant="bordered" aria-label={t('common.language')}>
      {ALL_LOCALES.map((locale) => (
        <Button
          key={locale}
          onPress={() => {
            void changeLanguage(locale);
            onChange?.(locale);
          }}
          color={locale === active ? 'primary' : 'default'}
          variant={locale === active ? 'solid' : 'bordered'}
          className={locale === active ? undefined : unselectedClass}
          aria-pressed={locale === active}
          aria-label={labelFor(locale)}
          lang={locale}
        >
          {labelFor(locale)}
        </Button>
      ))}
    </ButtonGroup>
  );
}
